import React, { useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import { saveSearch } from "@app/component/leadFinder/store/actions";
import {
  getLeadFinderSavedSearches,
  getLeadFinderSearch,
} from "@app/component/leadFinder/store/selectors";
import Interactive from "@app/component/map/core/controls/Interactive";
import { getLeadGenPlan, isCurrentUserOwner, isLeadFinderSaveEnabled } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import MapViewportState from "@app/types/map/MapViewportState";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import ProcessingModal from "../ProcessingModal";
import SearchAreaBar from "../SearchPopover/components/SearchAreaBar";

import SavedSearchesModal from "./SavedSearchesModal";
import styles from "./SearchBanner.module.scss";

const messages = defineMessages({
  exit: {
    id: "leadFinder.searchBanner.exit",
    defaultMessage: "Exit Lead Finder",
    description: "Exit Lead Finder button title",
  },
  save: {
    id: "leadFinder.searchBanner.save",
    defaultMessage: "Save this search",
    description: "Save this search button title",
  },
});

interface Props {
  /** Hide the banner (e.g. while a lasso path is being drawn on the map). Defaults to shown. */
  hidden?: boolean;
  /** Called right before a re-search runs from the edit-search popover. */
  onBeforeSearch?: () => void;
  /** Leave Lead Finder. Map exits the mode; Route hides the results panel. */
  onExit: () => void;
  /** Called after a re-search succeeds, with the results bounds when available. */
  onSearchSuccess: (bounds?: google.maps.LatLngBounds) => void;
  viewport?: MapViewportState;
}

const SearchBanner = ({
  canSave,
  hidden = false,
  isOwner,
  leadGenPlan,
  onBeforeSearch,
  onExit,
  onSearchSuccess,
  query,
  savedSearches,
  saveSearch,
  viewport,
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const handleSaveSearch = useCallback(() => {
    if (canSave && query) {
      saveSearch({ search: query });
    }
    analytics.clicked(["Save this search"]);
    if (!canSave) {
      showLeadFinderUpsell(leadGenPlan, intl, isOwner);
    }
  }, [canSave, query, analytics, leadGenPlan, intl, isOwner, saveSearch]);

  const handleExit = useCallback(() => {
    onExit();
    analytics.cancelled("");
  }, [analytics, onExit]);

  const isSaveVisible = !!query && !savedSearches.includes(query);

  return hidden ? null : (
    <div className={styles.container}>
      <div className={styles.content}>
        {query && (
          <Interactive>
            <SearchAreaBar
              analyticsIssuer={analytics}
              onBeforeSearch={onBeforeSearch}
              onSearchSuccess={onSearchSuccess}
              viewport={viewport}
            />
            <ProcessingModal />
          </Interactive>
        )}
      </div>

      <div className={styles.action}>
        {isSaveVisible && (
          <Interactive>
            <Button onClick={handleSaveSearch} type="filled">
              {intl.formatMessage(messages.save)}
            </Button>
          </Interactive>
        )}

        <Interactive>
          <Button onClick={handleExit}>{intl.formatMessage(messages.exit)}</Button>
        </Interactive>

        <SavedSearchesModal />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canSave: isLeadFinderSaveEnabled(state),
  isOwner: isCurrentUserOwner(state),
  leadGenPlan: getLeadGenPlan(state),
  query: getLeadFinderSearch(state),
  savedSearches: getLeadFinderSavedSearches(state),
});

const mapDispatchToProps = {
  saveSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchBanner);
